import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, CheckCircle2, ClipboardCheck } from "lucide-react";
import { api, formatApiError, gbp } from "@/lib/api";
import { inputClass } from "@/lib/constants";
import { toast } from "sonner";

const CATEGORY_NAMES = {
  "brownies-and-blondies": "Brownies & Blondies",
  "cookies-and-biscuits": "Cookies & Biscuits",
  "bake-at-home-cookie-dough": "Bake-at-Home Cookie Dough",
  "mixed-boxes": "Mixed Boxes",
  "seasonal-specials": "Seasonal Specials",
  "celebration-bakes": "Celebration Bakes",
};

const StatusPill = ({ ok, okText, badText, testid }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${ok ? "bg-green-500/15 text-green-400" : "bg-amber-500/15 text-amber-300"}`}
    data-testid={testid}
  >
    {ok ? <CheckCircle2 className="h-3 w-3" aria-hidden="true" /> : <AlertTriangle className="h-3 w-3" aria-hidden="true" />}
    {ok ? okText : badText}
  </span>
);

const STATUS_LABELS = {
  unreviewed: "Not reviewed yet",
  contains_regulated_allergens: "Contains regulated allergens",
  none_of_14_as_ingredients: "None of the UK 14 regulated allergens are used as ingredients",
};

export default function AdminCatalogueReview() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [migration, setMigration] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [migrating, setMigrating] = useState(false);

  const loadRows = () => api.get("/admin/catalogue/review").then((r) => setRows(r.data)).catch(() => setError("Could not load the review list."));
  const loadMigration = () => api.get("/admin/products/migration/allergen-declarations").then((r) => setMigration(r.data)).catch(() => {});
  useEffect(() => {
    loadRows();
    loadMigration();
  }, []);

  const runMigration = async () => {
    setMigrating(true);
    try {
      const r = await api.post("/admin/products/migration/allergen-declarations", { confirm: confirmText });
      toast.success(`Declarations set for ${r.data.changed} product${r.data.changed === 1 ? "" : "s"}`);
      setConfirmText("");
      setMigration(r.data);
      loadRows();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMigrating(false);
    }
  };

  if (error) return <p className="text-sm text-red-400" data-testid="review-error">{error}</p>;
  if (!rows) return <p className="text-sm text-cream-muted" data-testid="review-loading">Loading review…</p>;

  const needsReview = rows.filter((r) => r.owner_review_flags.length > 0 || r.ingredients_missing || !r.allergen_information_verified || !r.ingredient_information_verified || r.allergen_declaration_status === "unreviewed");
  const pendingMigration = migration?.products?.filter((p) => !p.already_migrated) || [];

  return (
    <div className="space-y-6" data-testid="catalogue-review-page">
      <div>
        <h1 className="font-serif text-2xl font-semibold text-cream-text">Catalogue Review</h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-cream-muted">
          Every product from the price-list import, side by side with what still needs your confirmation.
          Open each product, check it against the actual recipe and packaging, then verify its ingredients
          and allergen information individually. There is no bulk verify — each bake must be checked on its own.
          Nothing here can be bought by customers until its allergen information is verified.
        </p>
        <p className="mt-2 text-xs font-bold text-amber-300" data-testid="review-summary">
          {needsReview.length} of {rows.length} products need your review before they can go on sale.
        </p>
      </div>

      {migration && pendingMigration.length > 0 && (
        <section className="rounded-3xl border border-gold/50 bg-choco-light p-5" data-testid="allergen-migration-panel">
          <h2 className="flex items-center gap-2 font-serif text-lg font-semibold text-cream-text">
            <ClipboardCheck className="h-5 w-5 text-gold" aria-hidden="true" /> One-time setup: explicit allergen declarations
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-cream-muted">
            The shop now records an explicit allergen declaration for every product: “contains regulated allergens”,
            “none of the UK 14 regulated allergens are used as ingredients”, or “not reviewed yet”. Review each row below
            before confirming — nothing changes until you do. Products with declared allergens keep their existing
            verification; anything uncertain becomes “not reviewed yet” and must be declared and verified again by you.
            No product is ever marked as free of allergens automatically.
          </p>
          <ul className="mt-4 space-y-2">
            {pendingMigration.map((p) => (
              <li key={p.id} className="rounded-2xl border border-choco-line bg-choco p-4" data-testid={`migration-row-${p.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-cream-text">{p.name}</p>
                  {p.verification_preserved ? (
                    <span className="rounded-full bg-green-500/15 px-2.5 py-0.5 text-[11px] font-bold text-green-400" data-testid={`migration-preserved-${p.id}`}>Existing verification will be preserved</span>
                  ) : (
                    <span className="rounded-full bg-amber-500/15 px-2.5 py-0.5 text-[11px] font-bold text-amber-300" data-testid={`migration-fresh-${p.id}`}>Will need a fresh declaration &amp; verification</span>
                  )}
                </div>
                <p className="mt-1.5 text-xs text-cream-muted">
                  Now: {p.current.allergens.length > 0 ? `${p.current.allergens.length} declared allergen${p.current.allergens.length === 1 ? "" : "s"} (${p.current.allergens.join(", ")})` : "no allergens declared"}
                  {p.current.verified ? `, verified${p.current.verified_by ? ` by ${p.current.verified_by}` : ""}` : ", not verified"}
                </p>
                <p className="mt-1 text-xs font-semibold text-gold" data-testid={`migration-proposed-${p.id}`}>
                  Will become: {STATUS_LABELS[p.proposed_status]}
                </p>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <label className="text-xs font-semibold text-cream-muted">
              Type <code className="rounded bg-choco px-1.5 py-0.5 text-[11px] text-gold">{migration.confirm_phrase}</code> to confirm
              <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className={`${inputClass} mt-1.5 w-64`} data-testid="migration-confirm-input" />
            </label>
            <button type="button" onClick={runMigration} disabled={migrating || confirmText !== migration.confirm_phrase}
              className="mt-4 rounded-full bg-gold px-6 py-2.5 text-sm font-bold text-choco hover:bg-gold-hover disabled:opacity-40"
              data-testid="migration-apply-button">
              {migrating ? "Applying…" : `Apply to ${pendingMigration.length} product${pendingMigration.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </section>
      )}
      {migration && pendingMigration.length === 0 && (
        <p className="rounded-2xl border border-green-500/30 bg-green-500/10 p-3 text-xs font-semibold text-green-300" data-testid="migration-complete">
          Every product has an explicit allergen declaration — the one-time setup is complete.
        </p>
      )}

      <div className="space-y-4">
        {rows.map((r) => (
          <article key={r.id} className="rounded-3xl border border-choco-line bg-choco-light p-5" data-testid={`review-row-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="font-serif text-lg font-semibold text-cream-text" data-testid={`review-name-${r.id}`}>{r.name}</h2>
                <p className="text-xs text-cream-muted">{CATEGORY_NAMES[r.category_slug] || r.category_slug}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <StatusPill ok={r.active} okText="Published" badText="Draft (not on sale)" testid={`review-publish-${r.id}`} />
                <StatusPill ok={r.allergen_information_verified} okText="Allergens verified" badText="Allergens NOT verified" testid={`review-allergen-status-${r.id}`} />
                <StatusPill ok={r.ingredient_information_verified} okText="Ingredients verified" badText="Ingredients NOT verified" testid={`review-ingredient-status-${r.id}`} />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wide text-cream-muted">Sizes &amp; prices</h3>
                {r.options.length > 0 ? (
                  <ul className="mt-1 space-y-0.5 text-sm text-cream-text" data-testid={`review-options-${r.id}`}>
                    {r.options.map((o, i) => (
                      <li key={i} className="flex justify-between gap-3"><span>{o.label}</span><span className="font-semibold">{gbp(o.price_pence)}</span></li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-1 text-sm text-cream-text" data-testid={`review-price-${r.id}`}>{gbp(r.price_pence)}</p>
                )}
                {r.dietary_labels.length > 0 && (
                  <p className="mt-2 text-xs text-cream-muted capitalize" data-testid={`review-labels-${r.id}`}>Labels: {r.dietary_labels.join(", ")}</p>
                )}
              </div>
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wide text-cream-muted">Allergen declaration</h3>
                <p className="mt-1 text-sm capitalize text-cream-text" data-testid={`review-allergens-${r.id}`}>
                  {r.allergen_declaration_status === "none_of_14_as_ingredients"
                    ? "None of the UK 14 regulated allergens are used as ingredients"
                    : r.allergens.length > 0
                      ? `Contains: ${r.allergens.join(", ")}`
                      : "Not reviewed yet"}
                </p>
                {r.may_contain?.length > 0 && (
                  <p className="mt-1 text-xs capitalize text-amber-300" data-testid={`review-may-contain-${r.id}`}>
                    May contain: {r.may_contain.join(", ")}
                  </p>
                )}
                {r.additive_warnings.length > 0 && (
                  <ul className="mt-2 space-y-1" data-testid={`review-additives-${r.id}`}>
                    {r.additive_warnings.map((w, i) => <li key={i} className="text-xs text-amber-300">{w}</li>)}
                  </ul>
                )}
              </div>
            </div>

            {(r.owner_review_flags.length > 0 || r.ingredients_missing) && (
              <div className="mt-4 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4" data-testid={`review-flags-${r.id}`}>
                <h3 className="text-xs font-bold uppercase tracking-wide text-amber-300">Needs your confirmation</h3>
                <ul className="mt-1.5 list-disc space-y-1 pl-5 text-sm text-cream-text">
                  {r.owner_review_flags.map((f, i) => <li key={i} data-testid={`review-flag-${r.id}-${i}`}>{f}</li>)}
                  {r.ingredients_missing && <li data-testid={`review-ingredients-missing-${r.id}`}>Full ingredient list required — currently placeholder copy.</li>}
                </ul>
              </div>
            )}

            <Link
              to={`/admin/products/${r.id}`}
              className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-gold px-4 py-2 text-xs font-bold text-gold hover:bg-gold hover:text-choco"
              data-testid={`review-open-${r.id}`}
            >
              Open product to correct &amp; verify <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </article>
        ))}
      </div>
    </div>
  );
}
